export const KNOWLEDGE_BASE_LIST_PATH = "/knowledge-base";

export const knowledgeArticleDetailPath = (uuid: string): string => `${KNOWLEDGE_BASE_LIST_PATH}/${uuid}`;

export const KNOWLEDGE_ARTICLE_SECTION_QUERY = "section";

const KNOWLEDGE_UUID_PATTERN = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

/** Hash-router article href Ask AI may emit: `{origin}/#/knowledge-base/{uuid}` plus optional `?section={uuid}`. */
export const KNOWLEDGE_ARTICLE_HREF_PATTERN = new RegExp(
    `^(?:https?:\\/\\/[^\\s/?#]+)?\\/#\\/knowledge-base\\/${KNOWLEDGE_UUID_PATTERN}\\/?(?:\\?${KNOWLEDGE_ARTICLE_SECTION_QUERY}=${KNOWLEDGE_UUID_PATTERN})?$`,
    "i"
);

export const knowledgeArticleSectionDomId = (sectionUuid: string): string => `knowledge-section-${sectionUuid}`;

/** Wait for smooth-scroll to settle, then play three on/off pulses. */
export const KNOWLEDGE_SECTION_ANCHOR_BLINK_DELAY_MS = 400;
export const KNOWLEDGE_SECTION_ANCHOR_BLINK_COUNT = 3;
export const KNOWLEDGE_SECTION_ANCHOR_BLINK_ON_MS = 400;
export const KNOWLEDGE_SECTION_ANCHOR_BLINK_OFF_MS = 220;

export const KNOWLEDGE_ARTICLE_CREATE_PATH = `${KNOWLEDGE_BASE_LIST_PATH}/new`;

export const knowledgeArticleEditPath = (uuid: string): string => `${KNOWLEDGE_BASE_LIST_PATH}/${uuid}/edit`;

export const DEFAULT_KNOWLEDGE_ARTICLE_ICON = "pi pi-book";

export const DEFAULT_KNOWLEDGE_CATEGORY_ICON = "pi pi-folder";

/** Prime icon class from API (`pi pi-*`, or a single token like `pi-book`). */
export const resolveKnowledgeIconClass = (icon: string | null | undefined, fallback: string): string => {
    const raw = icon?.trim();
    if (!raw) {
        return fallback;
    }
    if (raw.includes(" ")) {
        return raw;
    }
    if (raw.startsWith("pi-")) {
        return `pi ${raw}`;
    }
    return raw;
};

export const resolveKnowledgeArticleIconClass = (icon?: string | null): string =>
    resolveKnowledgeIconClass(icon, DEFAULT_KNOWLEDGE_ARTICLE_ICON);

export const resolveKnowledgeCategoryIconClass = (icon?: string | null): string =>
    resolveKnowledgeIconClass(icon, DEFAULT_KNOWLEDGE_CATEGORY_ICON);

export const KNOWLEDGE_ARTICLE_STATUS = {
    DRAFT: "DRAFT",
    PUBLISHED: "PUBLISHED",
    ARCHIVED: "ARCHIVED",
} as const;

/** "17 sep 2026, 14:55" in the browser's locale; empty when the value is missing or unparseable. */
export const formatKnowledgeArticleTimestamp = (value?: string | null): string => {
    if (!value) {
        return "";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return "";
    }
    return parsed.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/** Default server page size for the knowledge-base article list (rows dropdown: 10/25/50/100). */
export const KNOWLEDGE_ARTICLE_PAGE_SIZE = 10;

/** Server page size for the overview's "pinned & featured" card grid (`spotlightOnly` list). */
export const KNOWLEDGE_ARTICLE_SPOTLIGHT_PAGE_SIZE = 8;

/** Below this width the overview sidebar becomes a category dropdown. */
export const KNOWLEDGE_BASE_NARROW_MAX_WIDTH = 900;

/** Sentinel `optionValue` for "all categories" in the mobile/narrow category dropdown. */
export const KNOWLEDGE_BASE_ALL_CATEGORIES_VALUE = "all";

/** Home pinned/featured cards: first page of the same GET list endpoint. */
export const KNOWLEDGE_ARTICLE_HOME_PAGE_SIZE = 4;

export const KNOWLEDGE_ARTICLE_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

/** CrudController returns a bare list when page/size are omitted, otherwise a Spring Page with content. */
export const unwrapCrudList = <T,>(data: T[] | { content?: unknown } | null | undefined): T[] => {
    if (Array.isArray(data)) {
        return data as T[];
    }
    return (data?.content as T[] | undefined) ?? [];
};

export const unwrapCrudListTotal = <T,>(data: T[] | { content?: unknown; totalElements?: number } | null | undefined): number => {
    if (!data) {
        return 0;
    }
    if (Array.isArray(data)) {
        return data.length;
    }
    return Math.max(0, Number(data.totalElements ?? (data.content as unknown[] | undefined)?.length ?? 0));
};

export const isFeaturedActive = (article: {
    featured?: boolean;
    featuredExpiresAt?: string;
}): boolean => {
    if (!article.featured) {
        return false;
    }
    if (!article.featuredExpiresAt) {
        return true;
    }
    return new Date(article.featuredExpiresAt) > new Date();
};

export const isFeaturedExpired = (article: {
    featured?: boolean;
    featuredExpiresAt?: string;
}): boolean => {
    return !!article.featured && !!article.featuredExpiresAt && new Date(article.featuredExpiresAt) <= new Date();
};

const AVERAGE_READING_WORDS_PER_MINUTE = 200;

export const stripArticleHtml = (html: string): string =>
    html
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();

/** Removes a WYSIWYG `<img>` whose given data-* attribute matches `value` (asset uuid or pending upload id). */
export const stripImgByDataAttribute = (html: string, attribute: string, value: string): string => {
    if (!html || !value) {
        return html;
    }
    const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const escapedAttribute = attribute.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return html.replace(new RegExp(`<img\\b[^>]*\\b${escapedAttribute}="${escapedValue}"[^>]*>`, "gi"), "");
};

/** Auto-calculated from the article body — no stored/editable field, always reflects the current content. */
export const estimateReadingTimeMinutes = (body: string): number => {
    const wordCount = stripArticleHtml(body ?? "").split(" ").filter(Boolean).length;
    return Math.max(1, Math.ceil(wordCount / AVERAGE_READING_WORDS_PER_MINUTE));
};
